
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface LoginResult {
  success: boolean;
  message?: string;
  error?: any;
}

export const useAuth = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminChecked, setIsAdminChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const sessionCheckRef = useRef(false);
  const loginInProgressRef = useRef(false);
  const loginAttemptTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Memoize the check session function to avoid recreation
  const checkSession = useCallback(async () => {
    if (sessionCheckRef.current) return;
    sessionCheckRef.current = true;
    
    try {
      setIsLoading(true);
      
      // Check Supabase session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        setIsAdmin(false);
        setIsAdminChecked(true);
        return;
      }
      
      const session = sessionData?.session;
      
      if (session) {
        // User is logged in with Supabase
        const { data: userData, error: userError } = await supabase.auth.getUser();
        
        if (userError) {
          console.error('User error:', userError);
          setIsAdmin(false);
          setIsAdminChecked(true);
          return;
        }
        
        // Check if the user's email is an admin email
        const isAdminUser = userData?.user?.email === 'oro@jobbstore.se';
        
        console.log("Setting admin status:", isAdminUser);
        setIsAdmin(isAdminUser);
      } else {
        // No Supabase session
        setIsAdmin(false);
      }
      
      console.log("Auth status checked - isAdmin:", isAdmin);
      setIsAdminChecked(true);
    } catch (error) {
      console.error('Error checking auth session:', error);
      setIsAdmin(false);
      setIsAdminChecked(true);
    } finally {
      setIsLoading(false);
      sessionCheckRef.current = false; // Reset the flag to allow checking again if needed
    }
  }, [isAdmin]);

  useEffect(() => {
    checkSession();
    
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session) {
          // User is signed in - check if admin
          const isAdminUser = session.user.email === 'oro@jobbstore.se';
          setIsAdmin(isAdminUser);
        } else {
          // User is signed out
          setIsAdmin(false);
        }
        setIsAdminChecked(true);
      }
    );

    // Clean up subscription when component unmounts
    return () => {
      subscription.unsubscribe();
    };
  }, [checkSession]);

  const login = useCallback(async (email?: string, password?: string): Promise<LoginResult> => {
    // Prevent multiple concurrent login attempts
    if (loginInProgressRef.current) {
      console.log("Login already in progress, skipping...");
      return { success: false, message: "Login already in progress" };
    }
    
    // Clear any existing timeout
    if (loginAttemptTimeoutRef.current) {
      clearTimeout(loginAttemptTimeoutRef.current);
    }
    
    loginInProgressRef.current = true;
    
    try {
      setIsLoading(true);
      console.log("Attempting login with:", email ? "email provided" : "no email");
      
      // Require email and password for login
      if (!email || !password) {
        console.log("Login failed - email and password required");
        setTimeout(() => {
          toast({
            title: "Inloggning misslyckades",
            description: "E-post och lösenord krävs",
            variant: "destructive"
          });
        }, 100);
        
        return { success: false, message: "Email and password required" };
      }
      
      // Try Supabase login
      console.log("Attempting Supabase login");
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });
      
      if (error) {
        console.error("Supabase login error:", error);
        setTimeout(() => {
          toast({
            title: "Inloggning misslyckades",
            description: "Felaktig e-post eller lösenord",
            variant: "destructive"
          });
        }, 100);
        return { success: false, message: error.message };
      }
      
      if (data?.user) {
        console.log("Supabase login successful", data.user);
        // Check if the logged in user is an admin
        const isAdminUser = data.user.email === 'oro@jobbstore.se';
        
        if (isAdminUser) {
          setIsAdmin(true);
          
          setTimeout(() => {
            toast({
              title: "Inloggad",
              description: "Du är nu inloggad som administratör",
            });
          }, 100);
        } else {
          // Not an admin user
          setTimeout(() => {
            toast({
              title: "Behörighetsproblem",
              description: "Denna användare har inte administratörsbehörighet",
              variant: "destructive"
            });
          }, 100);
          
          // Sign out the user if not admin
          await supabase.auth.signOut();
          setIsAdmin(false);
          return { success: false, message: "Not an admin user" };
        }
        
        setIsAdminChecked(true);
        
        // Dispatch event asynchronously to notify other components
        setTimeout(() => {
          const event = new CustomEvent('admin-login-success');
          window.dispatchEvent(event);
        }, 200);
        
        return { success: true };
      }
      
      // If we get here, login has failed
      console.log("Login failed - unknown error");
      setTimeout(() => {
        toast({
          title: "Inloggning misslyckades",
          description: "Ett okänt fel uppstod",
          variant: "destructive"
        });
      }, 100);
      
      return { success: false, message: "Unknown error" };
    } catch (error: any) {
      console.error('Login error:', error);
      
      setTimeout(() => {
        toast({
          title: "Inloggningsfel",
          description: error.message || "Kontrollera dina inloggningsuppgifter och försök igen",
          variant: "destructive"
        });
      }, 100);
      
      return { success: false, error };
    } finally {
      setIsLoading(false);
      
      // Reset login progress flag after a delay to prevent rapid retries
      loginAttemptTimeoutRef.current = setTimeout(() => {
        loginInProgressRef.current = false;
        loginAttemptTimeoutRef.current = null;
      }, 2000);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log("Logging out user");
      
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      setIsAdmin(false);
      
      toast({
        title: "Utloggad",
        description: "Du är nu utloggad",
      });
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      
      // Ensure local state is cleared even if Supabase logout fails
      setIsAdmin(false);
      
      toast({
        title: "Utloggad",
        description: "Du är nu utloggad (med varningar)",
      });
      return { success: true };
    } finally {
      setIsLoading(false);
      // Reset session check ref to allow checking again
      sessionCheckRef.current = false;
      setIsAdminChecked(true);
    }
  }, []);

  return {
    isAdmin,
    isAdminChecked,
    isLoading,
    login,
    logout
  };
};
